import {SagaIterator} from 'redux-saga';
import {propOr} from 'ramda';
import {sendHashedEmailEvent} from '@optimaxdev/analytics/mobile';

import {APP, LOCATION_CHANGED, ROUTER} from 'constants/actions';
import {ActionType} from 'constants/flow/flowTypes';
import {dynamicRoutes} from 'constants/router/router';

import {takeEvery, fork, call, cancel, select} from 'redux-saga/effects';
import {fetchProduct} from 'sagas/product/productData/productData';
import {fetchCategory} from 'sagas/category/category';
import {getCustomer} from 'reducers/oAuth/oAuth';

import {sendAnalyticsByPage} from './sendAnalyticsByPage/sendAnalyticsByPage';
import {goTo404Page, saveLocation, savePage} from './saveLocation/saveLocation';
import dynamicYieldSagaCustomer from './sendAnalyticsToDY/sendAnalyticsToDY';
import {menuSaga} from './menu/menu';
import {loadStoreInfo} from './actionsForChangePage/actionsForChangePage';
import {localStore} from 'libs/storage/storage';
import {sendEventToDataLayerForCustomerData} from 'libs/analytics/analytics';
import {createEventChannel} from 'libs/eventChannel/eventChannel';

type GetRouterInfoType = {
    deleted: boolean;
    deletedResponseCode: number;
    disableResponseCode: number | null;
    id: number;
    path: string;
    redirect: null | string;
    storeId: number;
    type: string;
};

type SetRouterInfoType = {
    code: null | string;
    page: string;
    params: {id: number};
};

type DynamicRouteType = {
    page: string;
    params: {dyType: string};
};

/**
 * Fetching dynamic content of certain pages
 * @param {string} page - Page type
 * @param {number} id - Product or category or CMS page id
 */
export function* fetchDynamicContent(page: string, id: number): SagaIterator {
    switch (page) {
        case 'product': {
            yield fork(fetchProduct, id);
            break;
        }
        case 'category': {
            yield fork(fetchCategory, id);
            break;
        }
        case 'page':
            break;
        default:
            yield cancel();
    }

    yield fork(savePage, {
        ...(propOr({}, page)(dynamicRoutes) as DynamicRouteType),
        id,
    });
}

/**
 * Handle the get router info action.
 * @param {ActionType<AxiosPromise>} action - Action
 */
export function* handleGetRouterInfo({
    payload: {data},
}: ActionType<{data: GetRouterInfoType}>): SagaIterator {
    if (!['product', 'category', 'page'].includes(data.type)) yield cancel();
    yield call(fetchDynamicContent, data.type, data.id);
}

/**
 * Handle the set router info action.
 * @param {ActionType<AxiosPromise>} action - Action
 */
export function* handleSetRouterInfo(action: ActionType<SetRouterInfoType>): SagaIterator {
    if (!['product', 'category', 'page'].includes(action.payload.page)) yield cancel();
    yield call(fetchDynamicContent, action.payload.page, action.payload.params.id);
}

/**
 * Return user email from localStorage or bronto
 * @return {string} - user email or empty string
 */
export function* getUserEmailFromStorageOrBronto(): SagaIterator {
    const userEmail = yield call([localStore, 'get'], 'email');

    return userEmail;
}

/**
 * Set user data to dataLayer
 * @param {string} emailAfterSubscription - user email after subscription
 */
export function* setUserDataToDataLayer(emailAfterSubscription = ''): SagaIterator {
    const customer = yield select(getCustomer);
    if (emailAfterSubscription) yield call([localStore, 'set'], 'email', emailAfterSubscription);

    const userEmail = yield call(getUserEmailFromStorageOrBronto);

    yield call(sendEventToDataLayerForCustomerData, customer.isLoggedIn ? customer : userEmail);

    yield call(sendHashedEmailEvent, customer.email);
}

/**
 * App sagas
 */
export function* forkAppSagas(): SagaIterator {
    yield fork(dynamicYieldSagaCustomer);
    yield fork(menuSaga);
    yield fork(setUserDataToDataLayer);
    yield fork(loadStoreInfo);
}

/**
 * Begin of saga
 */
export default function* appSaga(): SagaIterator {
    const nativeRedirect = yield call(createEventChannel, 'postMessage:redirect');

    yield takeEvery(`${APP.GET_ROUTER_INFO}_FULFILLED`, handleGetRouterInfo);
    yield takeEvery(ROUTER.SET, handleSetRouterInfo);
    yield takeEvery(`${APP.GET_ROUTER_INFO}_REJECTED`, goTo404Page);
    yield takeEvery(LOCATION_CHANGED, saveLocation);
    yield takeEvery(nativeRedirect, saveLocation);
    yield takeEvery(APP.SAVE_LOCATION, sendAnalyticsByPage);
    yield fork(forkAppSagas);
}
